// packages/ui-kit/src/primitives/Toast.tsx
// C3F v18 — reference: design/system/components/feedback/Toast.{jsx,prompt.md}
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";

export type ToastTone = "neutral" | "success" | "warning" | "danger" | "info";

export interface ToastOptions {
  title?: string;
  tone?: ToastTone;
  /** ms before auto-dismiss (default 4000). */
  duration?: number;
  /** Makes the toast a clickable button (e.g. open a deep link). */
  onClick?: () => void;
  /**
   * Extra action inside the toast (e.g. a ghost `<Button size="sm">` with zero
   * props — `surface-night` guarantees it renders legibly). Ignored when
   * `onClick` makes the whole toast one button: buttons cannot nest.
   */
  action?: ReactNode;
}

interface ToastItem extends ToastOptions {
  id: number;
  message: ReactNode;
}

interface ToastApi {
  show: (message: ReactNode, opts?: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

// DS: the tone is a dot, not a colored bar; neutral gets Spectrum-2 (signature,
// single small mark — not a functional fill). Inside `surface-night` the state
// tokens resolve to their Night inks, made for exactly this dark surface.
const dotCls: Record<ToastTone, string> = {
  neutral: "bg-spectrum-2",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
};

/*
 * The toast is an always-Night surface — a LAYER above the page, not a surface
 * of it — so `surface-night` redeclares the whole token family for the subtree
 * (a prop-less ghost Button inside must just work). The rgba paper hairline is
 * the DS's own: on Night the canvas is the same #100D16 and --shadow-lg is
 * black, so without it the toast melts into the page. It floats, hence a more
 * marked border than cards. If you restyle, do not drop the border.
 */
const toastCls =
  "surface-night bg-canvas text-fg-default w-full text-left flex items-start gap-3 rounded-md border border-[rgba(244,240,233,.18)] px-4 py-3.5 shadow-lg [animation:px-toast_var(--dur-slow)_var(--ease-out)_both]";

function ToastBody({ item }: { item: ToastItem }) {
  // <span>s throughout: the clickable variant nests this inside a <button>.
  return (
    <span className="min-w-0 flex-1">
      {item.title && <span className="block text-sm font-semibold">{item.title}</span>}
      <span className={`block text-caption text-sidebar-muted ${item.title ? "mt-0.5" : ""}`}>
        {item.message}
      </span>
    </span>
  );
}

/**
 * App-wide toast host. Wrap the app once; anything inside can call `useToast()`
 * to push a transient, auto-dismissing message. One viewport, one look — the
 * unified replacement for ad-hoc toast renderers.
 *
 * Toast or Banner? A toast is the consequence of an action just performed and
 * leaves on its own; a lasting condition of the structure is a `Banner`.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ReadonlyArray<ToastItem>>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: ReactNode, opts: ToastOptions = {}) => {
      const id = ++nextId.current;
      setItems((cur) => [...cur, { id, message, ...opts }]);
      const duration = opts.duration ?? 4000;
      window.setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ show, dismiss }}>
      {children}
      {/* DS: bottom corner; on phones it rises above the 56px bottom bar + safe area. */}
      <div
        role="status"
        aria-live="polite"
        className="fixed bottom-[calc(3.5rem+env(safe-area-inset-bottom)+0.75rem)] right-3 z-[60] flex w-[min(92vw,420px)] flex-col gap-2.5 sm:bottom-5 sm:right-5"
      >
        {items.map((t) => {
          const dot = (
            <span
              aria-hidden
              className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dotCls[t.tone ?? "neutral"]}`}
            />
          );
          return t.onClick ? (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                dismiss(t.id);
                t.onClick?.();
              }}
              className={`${toastCls} transition-colors duration-fast hover:bg-hover`}
            >
              {dot}
              <ToastBody item={t} />
            </button>
          ) : (
            <div key={t.id} className={toastCls}>
              {dot}
              <div className="min-w-0 flex-1">
                <ToastBody item={t} />
                {t.action && <div className="mt-2.5">{t.action}</div>}
              </div>
              {/* Attenuated with a COLOR, never opacity (DS): sidebar-muted is
                  the message ink this surface already redeclares. */}
              <button
                type="button"
                aria-label="Chiudi"
                onClick={() => dismiss(t.id)}
                className="tap-target -mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-lede leading-none text-sidebar-muted transition-colors duration-fast hover:bg-hover hover:text-fg-default"
              >
                {"×"}
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a <ToastProvider>");
  return ctx;
}
